import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
  useMemo,
} from 'react'
import dayjs, { Dayjs } from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { getErrorMessage } from '@sportninja/common/utils/utils'
import {
  createSchedule,
  updateSchedule,
  updateScheduleSettings,
} from 'src/helpers/EntityCreationHelper'
import {
  SportSettings,
  StandingsSettings,
  StatType,
} from '@sportninja/common/types'
import { DaysmartSyncIntervalEnum } from '@sportninja/common/constants/DaysmartSyncInterval'
import { getImageThumbnailId } from '@sportninja/common/reducers/helpers'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import { MessageInstance } from 'antd/es/message/interface'
import { useHistory } from 'react-router-dom'
import { ROUTES } from '@sportninja/common/constants/app'
import Text from 'src/components/ts-components/Text'
import colors from '@sportninja/common/constants/appColors'
import {
  ALLOWED_IMAGE_TYPES,
  MAX_IMAGE_SIZE_MB,
} from '@sportninja/common/constants/imageRequirements'

const websiteRegex =
  /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/

const getStatsSortOrderStatId = (statsSection?: any): number | null => {
  const statId = statsSection?.sort_order?.stat_id
  return typeof statId === 'number' ? statId : null
}

dayjs.extend(utc)

export const enum ScheduleModalTabs {
  General = '0',
  Settings = '1',
  ContactInfo = '2',
  Daysmart = '3',
}

// Define the initial state interface
interface InitialState {
  is_tournament?: boolean
  daysmart_facilities_as_sub_resources?: boolean
  daysmart_send_invitation?: boolean
  daysmart_season_id?: string
  daysmart_sync_interval?: DaysmartSyncIntervalEnum
  daysmart_season_name?: string
  name_full?: string
  is_public?: boolean
  sport_id?: string
  image?: {
    full_path?: string
  }
  is_registration_on?: boolean
  is_youth_league?: boolean
  disable_team_chat?: boolean
  tax_rate?: number
  season_type?: any
  season_currency?: any
  online_registration_open?: boolean
  settings?: {
    tax_rate?: number
    season_type?: any
    season_currency?: any
    online_registration_open?: boolean
    game?: {
      scoring?: {
        scoring_values?: number[]
        require_time_game_event?: boolean
        require_goal_type?: boolean
        require_goal_configuration?: boolean
        assign_points_to_player?: boolean
      }
      timing?: {
        default_period_length?: number
        default_overtime_length?: number
        regulation_game_length?: number
      }
      signatures?: {
        skip_official?: boolean
        skip_team_official?: boolean
      }
      skip_lineup?: boolean
      require_officials?: boolean
      require_unique_jersey_numbers?: boolean
      require_waiver_signature?: boolean
      require_goalie?: boolean
    }
    stats?: {
      goals?: {
        per?: {
          forfeit?: number
        }
      }
      points?: {
        wins?: number
        losses?: number
        ties?: number
        overtime_loss?: number
        shootout_loss?: number
        overtime_win?: number
        forfeit_loss?: number
      }
      player?: {
        displayed?: number[]
      }
      goalie?: {
        displayed?: number[]
      }
    }
    standings?: {
      displayed?: number[]
      sort_order?: {
        stat_id?: number
        tie_breaker_stats?: number[]
      }
    }
  }
  starts_at?: string
  ends_at?: string
  abbreviation?: string
  generic_sport_id?: string
  // TODO: Add background_image to the initial state
  background_image?: {
    full_path?: string
  }
  email?: string
  website?: string
  phone?: string
  support_email?: string
  support_website?: string
  support_phone?: string
  is_archive?: boolean
}

// Define the types for state variables
interface ScheduleState {
  sports?: any[]
  displayCanlanForm?: boolean
  id?: string
  sportOptions: any[]
  genericId: string | null
  isLoading: boolean
  error: string | null
  privacySettings: 'public' | 'private'
  imageChanged: boolean
  isEdit: boolean
  name: string
  abbreviation: string
  taxRate: number | null
  selectedSport: string | null | undefined
  sportId: string | null | undefined
  genericSportId: string | null | undefined
  competitionDateRange: {
    starts_at: string | Dayjs
    ends_at: string | Dayjs
  }
  playoffCompetition: boolean
  imageUrl: string | null
  selectedSeasonType: any
  onlineRegistrationOpen: boolean
  selectedSeasonCurrency: any
  isRegistrationEnabled: boolean
  skipTeamOfficialsSignatures: boolean
  skipOfficialsSignatures: boolean
  skipStartingLineupSelection: boolean
  requireGameOfficials: boolean
  timingPeriodLength: number | null
  timingExtraPeriodLength: number | null
  regulationGameLength: number | null
  goalsPerForfeit: number | null
  forfeitLoss: number | null
  wins: number | null
  losses: number | null
  ties: number | null
  overtimerWin: number | null
  overtimeLoss: number | null
  shootoutLoss: number | null
  fieldErrors: string[]
  customError: any
  contactInfo: {
    email?: string | null | undefined
    website?: string | null | undefined
    phone?: string | null | undefined
    phone_number_country?: string | null | undefined
    support_email?: string | null | undefined
    support_website?: string | null | undefined
    support_phone?: string | null | undefined
    support_phone_number_country?: string | null | undefined
  }
  requireWaiverSignature: boolean
  isYouthLeague: boolean
  disableTeamChat: boolean
  isArchive: boolean
  defaultSortBy: number | null
  tiebreakerStats: number[]
  displayedStandings: number[]
  displayedPlayerStats: number[]
  displayedGoalieStats: number[]
  playerStatsDefaultSortBy: number | null
  goalieStatsDefaultSortBy: number | null
}

// Define the context type
interface ScheduleContextProps extends ScheduleState {
  clearCustomErrorByField: (field: string) => void
  setCustomErroByField: (field: string, message: string | null) => void
  scoringValues: number[]
  setScoringValues: React.Dispatch<React.SetStateAction<number[]>>
  activeTab: string
  setActiveTab: React.Dispatch<React.SetStateAction<string>>
  requireGoalkeeper: boolean
  setRequireGoalkeeper: React.Dispatch<React.SetStateAction<boolean>>
  requireRosters: boolean
  setRequireRosters: React.Dispatch<React.SetStateAction<boolean>>
  isSoccer: boolean
  isGenericSport: boolean
  backgroundImageChanged: boolean
  setBackgroundImageChanged: React.Dispatch<React.SetStateAction<boolean>>
  setSportOptions: React.Dispatch<React.SetStateAction<any[]>>
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>
  setError: React.Dispatch<React.SetStateAction<string | null>>
  setPrivacySettings: React.Dispatch<React.SetStateAction<'public' | 'private'>>
  setImageChanged: React.Dispatch<React.SetStateAction<boolean>>
  setName: React.Dispatch<React.SetStateAction<string>>
  setAbbreviation: React.Dispatch<React.SetStateAction<string>>
  setTaxRate: React.Dispatch<React.SetStateAction<number | null>>
  setSelectedSport: React.Dispatch<
    React.SetStateAction<string | null | undefined>
  >
  setSportId: React.Dispatch<React.SetStateAction<string | null>>
  setGenericSportId: React.Dispatch<React.SetStateAction<string | null>>
  setCompetitionDateRange: React.Dispatch<
    React.SetStateAction<{
      starts_at: string
      ends_at: string
    }>
  >
  setPlayoffCompetition: React.Dispatch<React.SetStateAction<boolean>>
  setImageUrl: React.Dispatch<React.SetStateAction<string | null>>
  setSelectedSeasonType: React.Dispatch<React.SetStateAction<any>>
  setOnlineRegistrationOpen: React.Dispatch<React.SetStateAction<boolean>>
  setSelectedSeasonCurrency: React.Dispatch<React.SetStateAction<any>>
  setIsRegistrationEnabled: React.Dispatch<React.SetStateAction<boolean>>
  setSkipTeamOfficialsSignatures: React.Dispatch<React.SetStateAction<boolean>>
  setSkipOfficialsSignatures: React.Dispatch<React.SetStateAction<boolean>>
  setSkipStartingLineupSelection: React.Dispatch<React.SetStateAction<boolean>>
  setRequireGameOfficials: React.Dispatch<React.SetStateAction<boolean>>

  setTimingPeriodLength: React.Dispatch<React.SetStateAction<number | null>>
  setTimingExtraPeriodLength: React.Dispatch<
    React.SetStateAction<number | null>
  >
  setRegulationGameLength: React.Dispatch<React.SetStateAction<number | null>>
  setGoalsPerForfeit: React.Dispatch<React.SetStateAction<number | null>>
  setForfeitLoss: React.Dispatch<React.SetStateAction<number | null>>
  setWins: React.Dispatch<React.SetStateAction<number | null>>
  setLosses: React.Dispatch<React.SetStateAction<number | null>>
  setTies: React.Dispatch<React.SetStateAction<number | null>>
  setOvertimeWin: React.Dispatch<React.SetStateAction<number | null>>
  setOvertimeLoss: React.Dispatch<React.SetStateAction<number | null>>
  setShootoutLoss: React.Dispatch<React.SetStateAction<number | null>>
  setFieldErrors: React.Dispatch<React.SetStateAction<string[]>>
  setCustomError: any
  onCancel: () => void
  onSubmit: () => Promise<any>
  beforeUpload: (file: any) => boolean
  handleChange: (info: any) => void
  handleChangeBackground: (info: any) => void
  backgroundImageUrl: string | null
  setBackgroundImageUrl: React.Dispatch<React.SetStateAction<string | null>>
  genericSports?: any[]
  daySmartSeasonId: string
  daySmartSeasonName: string
  daySmartSyncInterval: DaysmartSyncIntervalEnum
  daySmartSendInvitation: boolean
  setDaySmartSendInvitation: React.Dispatch<React.SetStateAction<boolean>>
  setDaySmartSyncInterval: React.Dispatch<
    React.SetStateAction<DaysmartSyncIntervalEnum>
  >
  daySmartFacilitiesAsSubResources: boolean
  setDaySmartFacilitiesAsSubResources: React.Dispatch<
    React.SetStateAction<boolean>
  >
  hasChangedSettings: boolean
  contactInfo: {
    email?: string | null | undefined
    website?: string | null | undefined
    phone?: string | null | undefined
    phone_number_country?: string | null | undefined
    support_email?: string | null | undefined
    support_website?: string | null | undefined
    support_phone?: string | null | undefined
    support_phone_number_country?: string | null | undefined
  }
  setContactInfo: React.Dispatch<
    React.SetStateAction<{
      email?: string | null | undefined
      website?: string | null | undefined
      phone?: string | null | undefined
      phone_number_country?: string | null | undefined
      support_email?: string | null | undefined
      support_website?: string | null | undefined
      support_phone?: string | null | undefined
      support_phone_number_country?: string | null | undefined
    }>
  >
  requireUniqueJerseyNumbers: boolean
  setRequireUniqueJerseyNumbers: React.Dispatch<React.SetStateAction<boolean>>
  requireWaiverSignature: boolean
  setRequireWaiverSignature: React.Dispatch<React.SetStateAction<boolean>>
  onSubmitGeneralForm: () => Promise<any>
  onSubmitContactInfo: () => Promise<any>
  onSubmitSettings: () => Promise<any>
  onSubmitCustomization: () => Promise<any>
  onSubmitDaysmart: () => Promise<any>
  isSyncing: boolean
  setIsSyncing: React.Dispatch<React.SetStateAction<boolean>>
  message: MessageInstance | undefined
  hasFilledNameAndAbbreviationAndDates: boolean
  isYouthLeague: boolean
  setIsYouthLeague: React.Dispatch<React.SetStateAction<boolean>>
  disableTeamChat: boolean
  setDisableTeamChat: React.Dispatch<React.SetStateAction<boolean>>
  isArchive: boolean
  setIsArchive: React.Dispatch<React.SetStateAction<boolean>>
  requireTimeGameEvent: boolean
  setRequireTimeGameEvent: React.Dispatch<React.SetStateAction<boolean>>
  requireGoalType: boolean
  setRequireGoalType: React.Dispatch<React.SetStateAction<boolean>>
  requireGoalConfirmation: boolean
  setRequireGoalConfirmation: React.Dispatch<React.SetStateAction<boolean>>
  assignPointsToPlayer: boolean
  setAssignPointsToPlayer: React.Dispatch<React.SetStateAction<boolean>>
  allowTeamOfficialScoring: boolean
  setAllowTeamOfficialScoring: React.Dispatch<React.SetStateAction<boolean>>
  defaultSortBy: number | null
  setDefaultSortBy: React.Dispatch<React.SetStateAction<number | null>>
  tiebreakerStats: number[]
  setTiebreakerStats: React.Dispatch<React.SetStateAction<number[]>>
  displayedStandings: number[]
  setDisplayedStandings: React.Dispatch<React.SetStateAction<number[]>>
  displayedPlayerStats: number[]
  setDisplayedPlayerStats: React.Dispatch<React.SetStateAction<number[]>>
  displayedGoalieStats: number[]
  setDisplayedGoalieStats: React.Dispatch<React.SetStateAction<number[]>>
  setPlayerStatsDefaultSortBy: React.Dispatch<
    React.SetStateAction<number | null>
  >
  setGoalieStatsDefaultSortBy: React.Dispatch<
    React.SetStateAction<number | null>
  >
  statTypes: StatType[]
  playerStatsTypes: StatType[]
  fullAllStatTypes: StatType[]
  defaultStandingsSettingsForSport: StandingsSettings | null | undefined
}

const createAbbreviation = (companyName) => {
  let text = companyName.replace(/\s/g, '') // Remove all whitespaces
  // clear any special characters or emojis
  text = text.replace(/[^a-zA-Z0-9]/g, '')
  // Check if input is valid
  if (!text) {
    return '-' // Return an empty string if the input is empty or invalid
  }
  if (typeof text !== 'string') {
    return '-' // Return an empty string if the input is not a string
  }
  if (text.length < 5) {
    return text.toUpperCase() // Return the input in uppercase if it has less than 5 characters
  }
  // Get the first 5 characters of the text
  const abbreviation = text.slice(0, 5)

  // Ensure the abbreviation has at least one character and return it in uppercase
  return abbreviation.length > 0 ? abbreviation.toUpperCase() : ''
}

// Create the context
export const ScheduleContext = createContext<ScheduleContextProps | undefined>(
  undefined
)

// Create the provider component
const ScheduleProvider: React.FC<{
  children: ReactNode
  initialState?: InitialState
  sports?: any[]
  genericSports?: any[]
  id?: string
  displayCanlanForm?: boolean
  parentOrgId?: string
  defaultScheduleSettingsForAllSports: SportSettings[]
  existingScheduleSettings?: SportSettings
  onFinishTutorial?: () => void
  message?: MessageInstance
  closeForm: () => void
  triggerRefresh: () => void
  setIsCompetitionSetupModalOpen: React.Dispatch<React.SetStateAction<boolean>>
  setNewCompetitionId: React.Dispatch<React.SetStateAction<string | null>>
  allStatTypes: StatType[]
  allPlayerStatsTypes: StatType[]
  fullAllStatTypes: StatType[]
}> = ({
  children,
  initialState,
  sports,
  genericSports,
  id,
  displayCanlanForm,
  parentOrgId,
  defaultScheduleSettingsForAllSports,
  existingScheduleSettings = null,
  onFinishTutorial,
  message,
  closeForm,
  triggerRefresh,
  setIsCompetitionSetupModalOpen,
  setNewCompetitionId,
  allStatTypes,
  allPlayerStatsTypes,
  fullAllStatTypes,
}) => {
  const [activeTab, setActiveTab] = useState('0')

  // @ts-ignore
  const history = useHistory()
  const [sportOptions, setSportOptions] = useState<any[]>(() =>
    sports && genericSports
      ? [
          ...sports
            .map((option) => ({
              ...option,
              name: `${option.name_full} (Full Stats)`,
              is_generic: false,
            }))
            .filter((option) => !option.name.toLowerCase().includes('generic')),
          ...genericSports.map((option) => ({
            ...option,
            sport_id: sports?.find((sport) => sport.name === 'generic')?.id,
            name: `${option.name} (Basic Stats)`,
            is_generic: true,
          })),
        ]
      : []
  )
  const genericId = sports?.find((sport) =>
    sport?.name.toLowerCase().includes('generic')
  )?.id
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [privacySettings, setPrivacySettings] = useState<'public' | 'private'>(
    'public'
  )
  const [imageChanged, setImageChanged] = useState(false)
  const isEdit = Boolean(initialState?.name_full)
  const [name, setName] = useState('')
  const [abbreviation, setAbbreviation] = useState('')
  const [taxRate, setTaxRate] = useState<number | null>(null)
  const [selectedSport, setSelectedSport] = useState<string | null | undefined>(
    initialState ? initialState.sport_id : undefined
  )
  const [sportId, setSportId] = useState<string | null>(
    initialState?.sport_id || null
  )
  const [genericSportId, setGenericSportId] = useState<string | null>(null)
  const [competitionDateRange, setCompetitionDateRange] = useState({
    starts_at: '',
    ends_at: '',
  })

  const isSoccer = useMemo(() => {
    const s = sports?.find((sport) => sport.id === sportId)
    if (s?.name.toLowerCase().includes('soccer')) {
      return true
    }
    return false
  }, [sportId, sports])

  const isGenericSport = useMemo(() => {
    const s = sports?.find((sport) => sport.id === sportId)
    if (s?.name.toLowerCase().includes('generic')) {
      return true
    }
    return false
  }, [sportId, sports])

  const [requireGoalkeeper, setRequireGoalkeeper] = useState(true)
  const [requireRosters, setRequireRosters] = useState(true)

  const [playoffCompetition, setPlayoffCompetition] = useState(false)
  const [imageUrl, setImageUrl] = useState<string | null>(
    getImageThumbnailId(initialState) || null
  )
  const [backgroundImageUrl, setBackgroundImageUrl] = useState<string | null>(
    initialState?.background_image?.full_path || null
  )
  const [backgroundImageChanged, setBackgroundImageChanged] = useState(false)
  const [selectedSeasonType, setSelectedSeasonType] = useState<any>(null)
  const [onlineRegistrationOpen, setOnlineRegistrationOpen] = useState(false)
  const [selectedSeasonCurrency, setSelectedSeasonCurrency] =
    useState<any>(null)
  const [isRegistrationEnabled, setIsRegistrationEnabled] = useState<boolean>(
    initialState?.is_registration_on || isCanlan ? false : true
  )
  const [skipTeamOfficialsSignatures, setSkipTeamOfficialsSignatures] =
    useState(false)
  const [requireGameOfficials, setRequireGameOfficials] = useState(false)
  const [skipOfficialsSignatures, setSkipOfficialsSignatures] = useState(false)
  const [skipStartingLineupSelection, setSkipStartingLineupSelection] =
    useState(false)
  const [requireUniqueJerseyNumbers, setRequireUniqueJerseyNumbers] =
    useState(false)
  const [requireWaiverSignature, setRequireWaiverSignature] = useState(false)
  const [isYouthLeague, setIsYouthLeague] = useState<boolean>(
    initialState?.is_youth_league || false
  )
  const [disableTeamChat, setDisableTeamChat] = useState<boolean>(
    initialState?.disable_team_chat || false
  )

  const [timingPeriodLength, setTimingPeriodLength] = useState<number | null>(
    null
  )

  const [timingExtraPeriodLength, setTimingExtraPeriodLength] = useState<
    number | null
  >(null)
  const [regulationGameLength, setRegulationGameLength] = useState<
    number | null
  >(null)
  const [goalsPerForfeit, setGoalsPerForfeit] = useState<number | null>(null)
  const [forfeitLoss, setForfeitLoss] = useState<number | null>(null)
  const [wins, setWins] = useState<number | null>(null)
  const [losses, setLosses] = useState<number | null>(null)
  const [ties, setTies] = useState<number | null>(null)
  const [overtimerWin, setOvertimeWin] = useState<number | null>(null)
  const [overtimeLoss, setOvertimeLoss] = useState<number | null>(null)
  const [shootoutLoss, setShootoutLoss] = useState<number | null>(null)
  const [requireTimeGameEvent, setRequireTimeGameEvent] =
    useState<boolean>(false)
  const [requireGoalType, setRequireGoalType] = useState<boolean>(false)
  const [requireGoalConfirmation, setRequireGoalConfirmation] =
    useState<boolean>(false)
  const [assignPointsToPlayer, setAssignPointsToPlayer] =
    useState<boolean>(false)
  const [allowTeamOfficialScoring, setAllowTeamOfficialScoring] =
    useState<boolean>(false)
  const [fieldErrors, setFieldErrors] = useState<string[]>([])
  const [customError, setCustomError] = useState({
    name: null,
    sport: null,
    starts_at: null,
    ends_at: null,
    abbreviation: null,
    phone: null,
    website: null,
    email: null,
    support_email: null,
    support_website: null,
    support_phone: null,
    timingPeriodLength: null,
    timingExtraPeriodLength: null,
    regulationGameLength: null,
  })
  const daySmartSeasonName: string = initialState?.daysmart_season_name || ''
  const daySmartSeasonId: string = initialState?.daysmart_season_id || ''
  const [daySmartSyncInterval, setDaySmartSyncInterval] =
    useState<DaysmartSyncIntervalEnum>(
      initialState?.daysmart_sync_interval || DaysmartSyncIntervalEnum.NONE
    )
  const [daySmartSendInvitation, setDaySmartSendInvitation] = useState(
    initialState?.daysmart_send_invitation || false
  )
  const [
    daySmartFacilitiesAsSubResources,
    setDaySmartFacilitiesAsSubResources,
  ] = useState(initialState?.daysmart_facilities_as_sub_resources || false)

  // Scoring values - Score Settings
  const [scoringValues, setScoringValues] = useState<number[]>([])

  const [contactInfo, setContactInfo] = useState<{
    email?: string | null
    website?: string | null
    phone?: string | null
    phone_number_country?: string | null
    support_email?: string | null
    support_website?: string | null
    support_phone?: string | null
    support_phone_number_country?: string | null
  }>({})

  const [isSyncing, setIsSyncing] = useState(false)
  const hasFilledNameAndAbbreviationAndDates = useMemo(() => {
    return Boolean(
      name &&
        abbreviation &&
        name.length > 0 &&
        abbreviation.length > 0 &&
        competitionDateRange.starts_at &&
        competitionDateRange.ends_at &&
        selectedSport
    )
  }, [
    name,
    abbreviation,
    competitionDateRange.starts_at,
    competitionDateRange.ends_at,
    selectedSport,
  ])

  const invalidContactInfo = useMemo(() => {
    return (
      fieldErrors.includes('phone') ||
      fieldErrors.includes('email') ||
      fieldErrors.includes('website') ||
      fieldErrors.includes('support_phone') ||
      fieldErrors.includes('support_email') ||
      fieldErrors.includes('support_website')
    )
  }, [fieldErrors])

  const defaultStandingsSettingsForSport =
    defaultScheduleSettingsForAllSports.find(
      (setting) => setting.id === sportId
    )?.settings?.standings

  // Track initial values
  const [initialValues] = useState({
    syncInterval: daySmartSyncInterval,
    sendInvitation: daySmartSendInvitation,
    facilitiesAsSubResources: daySmartFacilitiesAsSubResources,
  })

  // Check if form has changes
  const hasChanges = useMemo(() => {
    return (
      initialValues.syncInterval !== daySmartSyncInterval ||
      initialValues.sendInvitation !== daySmartSendInvitation ||
      initialValues.facilitiesAsSubResources !==
        daySmartFacilitiesAsSubResources
    )
  }, [
    daySmartSyncInterval,
    daySmartSendInvitation,
    daySmartFacilitiesAsSubResources,
    initialValues,
  ])

  const hasChangedSettings = useMemo(() => {
    if (!existingScheduleSettings) {
      return false
    }
    if (
      existingScheduleSettings?.settings?.stats?.goals?.per?.forfeit !== null &&
      goalsPerForfeit !==
        existingScheduleSettings?.settings?.stats?.goals?.per?.forfeit
    ) {
      return true
    }
    if (
      (existingScheduleSettings?.settings?.stats?.points as any)
        ?.forfeit_loss !== null &&
      forfeitLoss !==
        (existingScheduleSettings?.settings?.stats?.points as any)?.forfeit_loss
    ) {
      return true
    }
    if (
      existingScheduleSettings?.settings?.stats?.points?.wins !== null &&
      wins !== existingScheduleSettings?.settings?.stats?.points?.wins
    ) {
      return true
    }
    if (
      existingScheduleSettings?.settings?.stats?.points?.losses !== null &&
      losses !== existingScheduleSettings?.settings?.stats?.points?.losses
    ) {
      return true
    }
    if (
      existingScheduleSettings?.settings?.stats?.points?.ties !== null &&
      ties !== existingScheduleSettings?.settings?.stats?.points?.ties
    ) {
      return true
    }
    if (
      existingScheduleSettings?.settings?.stats?.points?.overtime_win !==
        null &&
      overtimerWin !==
        existingScheduleSettings?.settings?.stats?.points?.overtime_win
    ) {
      return true
    }
    if (
      existingScheduleSettings?.settings?.stats?.points?.overtime_loss !==
        null &&
      overtimeLoss !==
        existingScheduleSettings?.settings?.stats?.points?.overtime_loss
    ) {
      return true
    }
    if (
      existingScheduleSettings?.settings?.stats?.points?.shootout_loss !==
        null &&
      shootoutLoss !==
        existingScheduleSettings?.settings?.stats?.points?.shootout_loss
    ) {
      return true
    }
    return false
  }, [
    existingScheduleSettings,
    goalsPerForfeit,
    forfeitLoss,
    wins,
    losses,
    ties,
    overtimerWin,
    overtimeLoss,
    shootoutLoss,
  ])

  const [isArchive, setIsArchive] = useState<boolean>(
    initialState?.is_archive || false
  )

  const [defaultSortBy, setDefaultSortBy] = useState<number | null>(
    initialState?.settings?.standings?.sort_order?.stat_id || null
  )
  const [tiebreakerStats, setTiebreakerStats] = useState<number[]>(
    initialState?.settings?.standings?.sort_order?.tie_breaker_stats || []
  )
  const [displayedStandings, setDisplayedStandings] = useState<number[]>(
    initialState?.settings?.standings?.displayed || []
  )
  const [displayedPlayerStats, setDisplayedPlayerStats] = useState<number[]>(
    initialState?.settings?.stats?.player?.displayed || []
  )
  const [displayedGoalieStats, setDisplayedGoalieStats] = useState<number[]>(
    initialState?.settings?.stats?.goalie?.displayed || []
  )
  const [playerStatsDefaultSortBy, setPlayerStatsDefaultSortBy] = useState<
    number | null
  >(getStatsSortOrderStatId(initialState?.settings?.stats?.player))
  const [goalieStatsDefaultSortBy, setGoalieStatsDefaultSortBy] = useState<
    number | null
  >(getStatsSortOrderStatId(initialState?.settings?.stats?.goalie))

  const statTypes = useMemo(() => {
    return allStatTypes.filter((stat) => stat.sport_id === sportId)
  }, [allStatTypes, sportId])

  const playerStatsTypes = useMemo(() => {
    return allPlayerStatsTypes.filter((stat) => stat.sport_id === sportId)
  }, [allPlayerStatsTypes, sportId])

  useEffect(() => {
    if (existingScheduleSettings && isEdit) {
      setRequireGoalkeeper(
        existingScheduleSettings?.settings?.game?.require_goalie || false
      )
      setRequireRosters(
        existingScheduleSettings?.settings?.game?.rosters?.require ?? true
      )
      setScoringValues(
        existingScheduleSettings?.settings?.game?.scoring?.scoring_values || []
      )
      setRequireGameOfficials(
        existingScheduleSettings?.settings?.game?.require_officials || false
      )
      setSkipOfficialsSignatures(
        existingScheduleSettings?.settings?.game?.signatures?.skip_official ||
          false
      )
      setSkipTeamOfficialsSignatures(
        existingScheduleSettings?.settings?.game?.signatures
          ?.skip_team_official || false
      )
      setSkipStartingLineupSelection(
        existingScheduleSettings?.settings?.game?.skip_lineup || false
      )
      setTimingPeriodLength(
        existingScheduleSettings?.settings?.game?.timing
          ?.default_period_length || 20
      )
      setTimingExtraPeriodLength(
        existingScheduleSettings?.settings?.game?.timing
          ?.default_overtime_length || 5
      )
      setRegulationGameLength(
        existingScheduleSettings?.settings?.game?.timing
          ?.regulation_game_length || 60
      )
      setGoalsPerForfeit(
        existingScheduleSettings?.settings?.stats?.goals?.per?.forfeit || 0
      )
      setForfeitLoss(
        ((existingScheduleSettings?.settings?.stats?.points as any)
          ?.forfeit_loss as number) || 0
      )
      setWins(existingScheduleSettings?.settings?.stats?.points?.wins || 0)
      setLosses(existingScheduleSettings?.settings?.stats?.points?.losses || 0)
      setTies(existingScheduleSettings?.settings?.stats?.points?.ties || 0)
      setOvertimeWin(
        existingScheduleSettings?.settings?.stats?.points?.overtime_win || 0
      )
      setOvertimeLoss(
        existingScheduleSettings?.settings?.stats?.points?.overtime_loss || 0
      )
      setShootoutLoss(
        existingScheduleSettings?.settings?.stats?.points?.shootout_loss || 0
      )
      setRequireUniqueJerseyNumbers(
        existingScheduleSettings?.settings?.game
          ?.require_unique_jersey_numbers || false
      )
      setRequireWaiverSignature(
        existingScheduleSettings?.settings?.game?.require_waiver_signature ||
          false
      )
      setRequireGoalType(
        existingScheduleSettings?.settings?.game?.scoring?.require_goal_type ||
          false
      )
      setRequireGoalConfirmation(
        existingScheduleSettings?.settings?.game?.scoring
          ?.require_goal_configuration || false
      )
      setRequireTimeGameEvent(
        existingScheduleSettings?.settings?.game?.scoring
          ?.require_time_game_event || false
      )
      setAssignPointsToPlayer(
        existingScheduleSettings?.settings?.game?.scoring
          ?.assign_points_to_player || false
      )
      setAllowTeamOfficialScoring(
        existingScheduleSettings?.settings?.game?.scoring
          ?.allow_team_official || false
      )
      setDefaultSortBy(
        existingScheduleSettings?.settings?.standings?.sort_order?.stat_id ||
          null
      )
      setTiebreakerStats(
        existingScheduleSettings?.settings?.standings?.sort_order
          ?.tie_breaker_stats || []
      )
      setDisplayedStandings(
        existingScheduleSettings?.settings?.standings?.displayed || []
      )
      setDisplayedPlayerStats(
        existingScheduleSettings?.settings?.stats?.player?.displayed || []
      )
      setDisplayedGoalieStats(
        existingScheduleSettings?.settings?.stats?.goalie?.displayed || []
      )
      setPlayerStatsDefaultSortBy(
        getStatsSortOrderStatId(
          existingScheduleSettings?.settings?.stats?.player
        )
      )
      setGoalieStatsDefaultSortBy(
        getStatsSortOrderStatId(
          existingScheduleSettings?.settings?.stats?.goalie
        )
      )
    }
  }, [existingScheduleSettings, isEdit])

  useEffect(() => {
    if (
      !defaultScheduleSettingsForAllSports ||
      defaultScheduleSettingsForAllSports.length === 0 ||
      !sportId ||
      isEdit ||
      fieldErrors?.length > 0
    ) {
      return
    }
    const defaultSettings = defaultScheduleSettingsForAllSports.find(
      (setting) => setting.id === sportId
    )
    setSkipOfficialsSignatures(
      defaultSettings?.settings?.game?.signatures?.skip_official || false
    )
    setSkipTeamOfficialsSignatures(
      defaultSettings?.settings?.game?.signatures?.skip_team_official || false
    )
    setSkipStartingLineupSelection(
      defaultSettings?.settings?.game?.skip_lineup || false
    )
    setRequireUniqueJerseyNumbers(
      defaultSettings?.settings?.game?.require_unique_jersey_numbers || false
    )
    setRequireWaiverSignature(
      defaultSettings?.settings?.game?.require_waiver_signature || false
    )
    setRequireTimeGameEvent(
      defaultSettings?.settings?.game?.scoring?.require_time_game_event || false
    )
    setRequireGoalType(
      defaultSettings?.settings?.game?.scoring?.require_goal_type || false
    )
    setRequireGoalConfirmation(
      defaultSettings?.settings?.game?.scoring?.require_goal_configuration ||
        false
    )
    setAssignPointsToPlayer(
      defaultSettings?.settings?.game?.scoring?.assign_points_to_player || false
    )
    setAllowTeamOfficialScoring(
      defaultSettings?.settings?.game?.scoring?.allow_team_official || false
    )
    setTimingPeriodLength(
      defaultSettings?.settings?.game?.timing?.default_period_length || 20
    )
    setTimingExtraPeriodLength(
      defaultSettings?.settings?.game?.timing?.default_overtime_length || 5
    )
    setRegulationGameLength(
      defaultSettings?.settings?.game?.timing?.regulation_game_length || 60
    )
    setRequireGameOfficials(
      defaultSettings?.settings?.game?.require_officials || false
    )
    setRequireGoalkeeper(
      defaultSettings?.settings?.game?.require_goalie || false
    )
    setRequireRosters(defaultSettings?.settings?.game?.rosters?.require ?? true)
    setScoringValues(
      defaultSettings?.settings?.game?.scoring?.scoring_values || []
    )
    setGoalsPerForfeit(
      defaultSettings?.settings?.stats?.goals?.per?.forfeit || 0
    )
    setForfeitLoss(
      ((defaultSettings?.settings?.stats?.points as any)
        ?.forfeit_loss as number) || 0
    )
    setWins(defaultSettings?.settings?.stats?.points?.wins || 0)
    setLosses(defaultSettings?.settings?.stats?.points?.losses || 0)
    setTies(defaultSettings?.settings?.stats?.points?.ties || 0)
    setOvertimeWin(defaultSettings?.settings?.stats?.points?.overtime_win || 0)
    setOvertimeLoss(
      defaultSettings?.settings?.stats?.points?.overtime_loss || 0
    )
    setShootoutLoss(
      defaultSettings?.settings?.stats?.points?.shootout_loss || 0
    )
    setDefaultSortBy(
      defaultSettings?.settings?.standings?.sort_order?.stat_id || null
    )
    setTiebreakerStats(
      defaultSettings?.settings?.standings?.sort_order?.tie_breaker_stats || []
    )
    setDisplayedStandings(defaultSettings?.settings?.standings?.displayed || [])
    setDisplayedPlayerStats(
      defaultSettings?.settings?.stats?.player?.displayed || []
    )
    setDisplayedGoalieStats(
      defaultSettings?.settings?.stats?.goalie?.displayed || []
    )
    setPlayerStatsDefaultSortBy(
      getStatsSortOrderStatId(defaultSettings?.settings?.stats?.player)
    )
    setGoalieStatsDefaultSortBy(
      getStatsSortOrderStatId(defaultSettings?.settings?.stats?.goalie)
    )
  }, [
    defaultScheduleSettingsForAllSports,
    fieldErrors?.length,
    isEdit,
    sportId,
  ])

  useEffect(() => {
    // We only want to set the abbreviation if it's not already set
    // aka if it's an edit, we don't want to overwrite the existing abbreviation
    if (name && name?.length > 0 && !initialState?.abbreviation) {
      setAbbreviation(createAbbreviation(name))
    }
  }, [initialState?.abbreviation, name])

  useEffect(() => {
    if (initialState && initialState?.starts_at && initialState?.ends_at) {
      setPlayoffCompetition(initialState?.is_tournament || false)
      setCompetitionDateRange({
        starts_at: dayjs(initialState?.starts_at).utc().format('YYYY-MM-DD'),
        ends_at: dayjs(initialState?.ends_at).utc().format('YYYY-MM-DD'),
      })
      setName(initialState?.name_full || '')
      setAbbreviation(initialState?.abbreviation || '')
      setSelectedSport(initialState?.sport_id)
      setPrivacySettings(initialState?.is_public ? 'public' : 'private')
      setImageUrl(getImageThumbnailId(initialState) || null)
      setBackgroundImageUrl(initialState?.background_image?.full_path || null)
      setIsRegistrationEnabled(initialState?.is_registration_on || false)
      setIsYouthLeague(initialState?.is_youth_league || false)
      setDisableTeamChat(initialState?.disable_team_chat || false)
      setTaxRate(
        initialState?.settings?.tax_rate || initialState?.tax_rate || null
      )
      setSelectedSeasonType(
        initialState?.settings?.season_type || initialState?.season_type || null
      )
      setSelectedSeasonCurrency(
        initialState?.settings?.season_currency ||
          initialState?.season_currency ||
          null
      )
      setOnlineRegistrationOpen(
        initialState?.settings?.online_registration_open ||
          initialState?.online_registration_open ||
          false
      )
      if (initialState?.generic_sport_id) {
        setGenericSportId(initialState?.generic_sport_id)
        const sport = sportOptions.find(
          (s) => s.id === initialState?.generic_sport_id
        )
        setSportId(sport?.sport_id)
        setSelectedSport(sport?.id)
      } else {
        setSportId(initialState?.sport_id || null)
        setSelectedSport(initialState?.sport_id)
        setGenericSportId(null)
      }
      initialState?.daysmart_sync_interval &&
        setDaySmartSyncInterval(initialState?.daysmart_sync_interval)
      initialState?.daysmart_send_invitation &&
        setDaySmartSendInvitation(initialState?.daysmart_send_invitation)
      initialState?.daysmart_facilities_as_sub_resources &&
        setDaySmartFacilitiesAsSubResources(
          initialState?.daysmart_facilities_as_sub_resources
        )
      initialState?.settings?.game?.signatures &&
        setSkipOfficialsSignatures(
          initialState?.settings?.game?.signatures?.skip_official || false
        )
      initialState?.settings?.game?.signatures &&
        setSkipTeamOfficialsSignatures(
          initialState?.settings?.game?.signatures?.skip_team_official || false
        )
      initialState?.settings?.game &&
        setSkipStartingLineupSelection(
          initialState?.settings?.game?.skip_lineup || false
        )
      initialState?.settings?.game &&
        setRequireUniqueJerseyNumbers(
          initialState?.settings?.game?.require_unique_jersey_numbers || false
        )
      initialState?.settings?.game &&
        setRequireWaiverSignature(
          initialState?.settings?.game?.require_waiver_signature || false
        )
      initialState?.settings?.game &&
        setRequireGameOfficials(
          initialState?.settings?.game?.require_officials || false
        )
      initialState?.settings?.game &&
        setRequireGoalkeeper(
          initialState?.settings?.game?.require_goalie || false
        )
      initialState?.settings?.game?.scoring &&
        setScoringValues(
          initialState?.settings?.game?.scoring?.scoring_values || []
        )
      initialState?.settings?.game?.timing &&
        setTimingPeriodLength(
          initialState?.settings?.game?.timing?.default_period_length || 20
        )
      initialState?.settings?.game?.timing &&
        setTimingExtraPeriodLength(
          initialState?.settings?.game?.timing?.default_overtime_length || 5
        )
      initialState?.settings?.game?.timing &&
        setRegulationGameLength(
          initialState?.settings?.game?.timing?.regulation_game_length || 60
        )
      initialState?.settings?.game?.scoring &&
        setRequireTimeGameEvent(
          initialState?.settings?.game?.scoring?.require_time_game_event ||
            false
        )
      initialState?.settings?.game?.scoring &&
        setRequireGoalType(
          initialState?.settings?.game?.scoring?.require_goal_type || false
        )
      initialState?.settings?.game?.scoring &&
        setRequireGoalConfirmation(
          initialState?.settings?.game?.scoring?.require_goal_configuration ||
            false
        )
      initialState?.settings?.game?.scoring &&
        setAssignPointsToPlayer(
          initialState?.settings?.game?.scoring?.assign_points_to_player ||
            false
        )
      initialState?.settings?.stats?.goals &&
        setGoalsPerForfeit(
          initialState?.settings?.stats?.goals?.per?.forfeit || 0
        )
      initialState?.settings?.stats?.points &&
        setForfeitLoss(initialState?.settings?.stats?.points?.forfeit_loss || 0)
      initialState?.settings?.stats?.points &&
        setWins(initialState?.settings?.stats?.points?.wins || 0)
      initialState?.settings?.stats?.points &&
        setLosses(initialState?.settings?.stats?.points?.losses || 0)
      initialState?.settings?.stats?.points &&
        setTies(initialState?.settings?.stats?.points?.ties || 0)
      initialState?.settings?.stats?.points &&
        setOvertimeWin(initialState?.settings?.stats?.points?.overtime_win || 0)
      initialState?.settings?.stats?.points &&
        setOvertimeLoss(
          initialState?.settings?.stats?.points?.overtime_loss || 0
        )
      initialState?.settings?.stats?.points &&
        setShootoutLoss(
          initialState?.settings?.stats?.points?.shootout_loss || 0
        )
      initialState?.email &&
        setContactInfo((prev) => ({ ...prev, email: initialState?.email }))
      initialState?.website &&
        setContactInfo((prev) => ({ ...prev, website: initialState?.website }))
      initialState?.phone &&
        setContactInfo((prev) => ({ ...prev, phone: initialState?.phone }))
      initialState?.support_email &&
        setContactInfo((prev) => ({
          ...prev,
          support_email: initialState?.support_email,
        }))
      initialState?.support_website &&
        setContactInfo((prev) => ({
          ...prev,
          support_website: initialState?.support_website,
        }))
      initialState?.support_phone &&
        setContactInfo((prev) => ({
          ...prev,
          support_phone: initialState?.support_phone,
        }))
      initialState?.settings?.standings?.sort_order?.stat_id &&
        setDefaultSortBy(initialState?.settings?.standings?.sort_order?.stat_id)
      initialState?.settings?.standings?.sort_order?.tie_breaker_stats &&
        setTiebreakerStats(
          initialState?.settings?.standings?.sort_order?.tie_breaker_stats
        )
      initialState?.settings?.stats?.player?.displayed &&
        setDisplayedPlayerStats(
          initialState?.settings?.stats?.player?.displayed
        )
      initialState?.settings?.stats?.goalie?.displayed &&
        setDisplayedGoalieStats(
          initialState?.settings?.stats?.goalie?.displayed
        )
      setPlayerStatsDefaultSortBy(
        getStatsSortOrderStatId(initialState?.settings?.stats?.player)
      )
      setGoalieStatsDefaultSortBy(
        getStatsSortOrderStatId(initialState?.settings?.stats?.goalie)
      )
    }
  }, [initialState, sportOptions])

  const onCancel = () => {
    setName('')
    setAbbreviation('')
    setTaxRate(null)
    setSelectedSport(null)
    setSportId(null)
    setGenericSportId(null)
    setCompetitionDateRange({
      starts_at: dayjs().utc().format('YYYY-MM-DD'),
      ends_at: dayjs().utc().format('YYYY-MM-DD'),
    })
    setPlayoffCompetition(false)
    setImageUrl(null)
    setSelectedSeasonType(null)
    setOnlineRegistrationOpen(false)
    setSelectedSeasonCurrency(null)
    setIsRegistrationEnabled(isCanlan ? false : true)
    setSkipTeamOfficialsSignatures(false)
    setSkipOfficialsSignatures(false)
    setSkipStartingLineupSelection(false)
    setRequireGameOfficials(false)
    setRequireGoalkeeper(true)
    setRequireUniqueJerseyNumbers(false)
    setRequireWaiverSignature(false)
    setRequireTimeGameEvent(false)
    setRequireGoalType(false)
    setRequireGoalConfirmation(false)
    setAssignPointsToPlayer(false)
    setTimingPeriodLength(20)
    setTimingExtraPeriodLength(5)
    setRegulationGameLength(60)
    setGoalsPerForfeit(null)
    setForfeitLoss(null)
    setWins(null)
    setLosses(null)
    setTies(null)
    setOvertimeWin(null)
    setOvertimeLoss(null)
    setShootoutLoss(null)
    setFieldErrors([])
    setCustomError({
      name: null,
      sport: null,
      starts_at: null,
      ends_at: null,
      abbreviation: null,
      phone: null,
      website: null,
      email: null,
      support_email: null,
      support_website: null,
      support_phone: null,
      timingPeriodLength: null,
      timingExtraPeriodLength: null,
      regulationGameLength: null,
    })
    setContactInfo({
      email: null,
      website: null,
      phone: null,
      support_email: null,
      support_website: null,
      support_phone: null,
    })
    setDefaultSortBy(null)
    setTiebreakerStats([])
    setDisplayedPlayerStats([])
    setDisplayedGoalieStats([])
    setPlayerStatsDefaultSortBy(null)
    setGoalieStatsDefaultSortBy(null)
  }

  const onSubmit = async () => {
    setIsLoading(true)
    setError(null)
    setFieldErrors([])
    setCustomError({
      name: null,
      sport: null,
      starts_at: null,
      ends_at: null,
      abbreviation: null,
      phone: null,
      website: null,
      support_email: null,
      support_website: null,
      support_phone: null,
      email: null,
      timingPeriodLength: null,
      timingExtraPeriodLength: null,
      regulationGameLength: null,
    })

    if (!isGenericSport) {
      if (timingPeriodLength === null || timingPeriodLength <= 0) {
        setCustomErroByField(
          'timingPeriodLength',
          'Timing period length is required and must be greater than 0'
        )
        setActiveTab(ScheduleModalTabs.Settings)
        setIsLoading(false)
        throw new Error(
          'Timing period length is required and must be greater than 0'
        )
      }
      if (timingExtraPeriodLength === null || timingExtraPeriodLength <= 0) {
        setCustomErroByField(
          'timingExtraPeriodLength',
          'Timing extra period length is required and must be greater than 0'
        )
        setActiveTab(ScheduleModalTabs.Settings)
        setIsLoading(false)
        throw new Error(
          'Timing extra period length is required and must be greater than 0'
        )
      }
      if (regulationGameLength === null || regulationGameLength <= 0) {
        setCustomErroByField(
          'regulationGameLength',
          'Regulation game length is required and must be greater than 0'
        )
        setActiveTab(ScheduleModalTabs.Settings)
        setIsLoading(false)
        throw new Error(
          'Regulation game length is required and must be greater than 0'
        )
      }
    }

    // Validation for Default Sort By fields
    // 1. Standings
    if (displayedStandings.length > 0 && !defaultSortBy) {
      setCustomErroByField('defaultSortBy', 'Default sort by is required')
      setActiveTab(ScheduleModalTabs.Settings)
      setIsLoading(false)
      throw new Error('Default sort by is required')
    }

    // 2. Player Stats
    if (displayedPlayerStats.length > 0 && !playerStatsDefaultSortBy) {
      const label = isSoccer ? 'player' : 'skater'
      setCustomErroByField(
        'playerStatsDefaultSortBy',
        `Default ${label} stats sort by is required`
      )
      setActiveTab(ScheduleModalTabs.Settings)
      setIsLoading(false)
      throw new Error(`Default ${label} stats sort by is required`)
    }

    // 3. Goalie Stats (only if not generic sport)
    if (
      !isGenericSport &&
      displayedGoalieStats.length > 0 &&
      !goalieStatsDefaultSortBy
    ) {
      const label = isSoccer ? 'keeper' : 'goalie'
      setCustomErroByField(
        'goalieStatsDefaultSortBy',
        `Default ${label} stats sort by is required`
      )
      setActiveTab(ScheduleModalTabs.Settings)
      setIsLoading(false)
      throw new Error(`Default ${label} stats sort by is required`)
    }

    // We should check both the website and support_website to check if they are valid URL
    // and if they contain http or https on the beginning. If they dont have, we should add it.
    let website = contactInfo?.website || null
    let supportWebsite = contactInfo?.support_website || null
    // Check if website is valid
    if (website && !websiteRegex.test(website)) {
      setCustomErroByField('website', 'Invalid website')
      setActiveTab(ScheduleModalTabs.ContactInfo)
      setIsLoading(false)
      throw new Error('Invalid website')
    }
    // Check if supportWebsite is valid
    if (supportWebsite && !websiteRegex.test(supportWebsite)) {
      setCustomErroByField('support_website', 'Invalid website')
      setActiveTab(ScheduleModalTabs.ContactInfo)
      setIsLoading(false)
      throw new Error('Invalid website')
    }
    if (website && !website?.startsWith('http')) {
      website = `https://${website}`
    }
    if (supportWebsite && !supportWebsite?.startsWith('http')) {
      supportWebsite = `https://${supportWebsite}`
    }

    let phone_number_country = contactInfo?.phone_number_country || null
    if (contactInfo?.phone) {
      if (!contactInfo?.phone_number_country) {
        phone_number_country = 'US'
      }
    }

    let support_phone_number_country =
      contactInfo?.support_phone_number_country || null
    if (contactInfo?.support_phone) {
      if (!contactInfo?.support_phone_number_country) {
        support_phone_number_country = 'US'
      }
    }

    // Enforce timing defaults before submission - never allow null or 0 values
    const finalTimingPeriodLength =
      timingPeriodLength && timingPeriodLength > 0 ? timingPeriodLength : 20
    const finalTimingExtraPeriodLength =
      timingExtraPeriodLength && timingExtraPeriodLength > 0
        ? timingExtraPeriodLength
        : 5
    const finalRegulationGameLength =
      regulationGameLength && regulationGameLength > 0
        ? regulationGameLength
        : 60

    try {
      let response
      if (!isEdit) {
        response = await createSchedule(
          {
            abbreviation: abbreviation,
            starts_at: competitionDateRange.starts_at,
            ends_at: competitionDateRange.ends_at,
            is_registration_on: isRegistrationEnabled,
            sport_type: sportOptions.find((sport) => sport.id === selectedSport)
              ?.name,
            sport_id: sportId || sportOptions[0]?.id,
            generic_sport_id: genericSportId,
            is_public: privacySettings === 'public' ? true : false,
            name: name,
            season_type: selectedSeasonType,
            season_currency: selectedSeasonCurrency,
            tax_rate: taxRate,
            online_registration_open: onlineRegistrationOpen,
            website: website,
            email: contactInfo?.email,
            phone: contactInfo?.phone,
            phone_country: phone_number_country,
            support_email: contactInfo?.support_email,
            support_website: supportWebsite,
            support_phone: contactInfo?.support_phone,
            support_phone_country: support_phone_number_country,
            is_youth_league: isYouthLeague,
            disable_team_chat: disableTeamChat,
            is_archive: isArchive,
          },
          parentOrgId,
          imageUrl,
          backgroundImageUrl,
          imageChanged,
          backgroundImageChanged
        )

        await updateScheduleSettings(response?.data?.id, {
          game: {
            scoring: {
              scoring_values: scoringValues,
              require_time_game_event: requireTimeGameEvent,
              require_goal_type: requireGoalType,
              require_goal_configuration: requireGoalConfirmation,
              assign_points_to_player: assignPointsToPlayer,
              allow_team_official: allowTeamOfficialScoring,
            },
            timing: {
              default_period_length: finalTimingPeriodLength,
              default_overtime_length: finalTimingExtraPeriodLength,
              regulation_game_length: finalRegulationGameLength,
            },
            signatures: {
              skip_official: skipOfficialsSignatures,
              skip_team_official: skipTeamOfficialsSignatures,
            },
            skip_lineup: skipStartingLineupSelection,
            require_officials: requireGameOfficials,
            require_unique_jersey_numbers: requireUniqueJerseyNumbers,
            require_waiver_signature: requireWaiverSignature,
            require_goalie: requireGoalkeeper,
            rosters: {
              require: requireRosters,
            },
          },
          stats: {
            goals: {
              per: {
                forfeit: goalsPerForfeit,
              },
            },
            points: {
              wins: wins,
              losses: losses,
              ties: ties,
              overtime_loss: overtimeLoss,
              shootout_loss: shootoutLoss,
              overtime_win: overtimerWin,
              forfeit_loss: forfeitLoss,
            },
            player: {
              displayed: displayedPlayerStats,
              sort_order: {
                stat_id: playerStatsDefaultSortBy,
              },
            } as any,
            goalie: {
              displayed: displayedGoalieStats,
              sort_order: {
                stat_id: goalieStatsDefaultSortBy,
              },
            } as any,
          },
          standings: {
            displayed: displayedStandings,
            sort_order: {
              stat_id: defaultSortBy,
              tie_breaker_stats: tiebreakerStats,
            },
          },
        })
        return response
      } else {
        response = await updateSchedule(
          {
            starts_at: competitionDateRange.starts_at,
            ends_at: competitionDateRange.ends_at,
            is_registration_on: isRegistrationEnabled,
            sport_type: sports?.find((sport) => sport.id === selectedSport)
              ?.name,
            is_public: privacySettings === 'public' ? true : false,
            name: name,
            name_full: name,
            season_type: selectedSeasonType,
            season_currency: selectedSeasonCurrency,
            tax_rate: taxRate,
            online_registration_open: onlineRegistrationOpen,
            daysmart_sync_interval: daySmartSyncInterval,
            daysmart_send_invitation: daySmartSendInvitation,
            daysmart_facilities_as_sub_resources:
              daySmartFacilitiesAsSubResources,
            abbreviation: abbreviation,
            website: website,
            email: contactInfo?.email,
            phone: contactInfo?.phone,
            phone_country: phone_number_country,
            support_email: contactInfo?.support_email,
            support_website: supportWebsite,
            support_phone: contactInfo?.support_phone,
            support_phone_country: support_phone_number_country,
            is_youth_league: isYouthLeague,
            disable_team_chat: disableTeamChat,
            is_archive: isArchive,
          },
          id,
          imageUrl,
          backgroundImageUrl,
          imageChanged,
          backgroundImageChanged
        )
        await updateScheduleSettings(id, {
          game: {
            scoring: {
              scoring_values: scoringValues,
              require_time_game_event: requireTimeGameEvent,
              require_goal_type: requireGoalType,
              require_goal_configuration: requireGoalConfirmation,
              assign_points_to_player: assignPointsToPlayer,
              allow_team_official: allowTeamOfficialScoring,
            },
            timing: {
              default_period_length: finalTimingPeriodLength,
              default_overtime_length: finalTimingExtraPeriodLength,
              regulation_game_length: finalRegulationGameLength,
            },
            signatures: {
              skip_official: skipOfficialsSignatures,
              skip_team_official: skipTeamOfficialsSignatures,
            },
            skip_lineup: skipStartingLineupSelection,
            require_officials: requireGameOfficials,
            require_unique_jersey_numbers: requireUniqueJerseyNumbers,
            require_waiver_signature: requireWaiverSignature,
            require_goalie: requireGoalkeeper,
            rosters: {
              require: requireRosters,
            },
          },
          stats: {
            goals: {
              per: {
                forfeit: goalsPerForfeit,
              },
            },
            points: {
              wins: wins,
              losses: losses,
              ties: ties,
              overtime_loss: overtimeLoss,
              shootout_loss: shootoutLoss,
              overtime_win: overtimerWin,
              forfeit_loss: forfeitLoss,
            },
            player: {
              displayed: displayedPlayerStats,
              sort_order: {
                stat_id: playerStatsDefaultSortBy,
              },
            } as any,
            goalie: {
              displayed: displayedGoalieStats,
              sort_order: {
                stat_id: goalieStatsDefaultSortBy,
              },
            } as any,
          },
          standings: {
            displayed: displayedStandings,
            sort_order: {
              stat_id: defaultSortBy,
              tie_breaker_stats: tiebreakerStats,
            },
          },
        })
      }
      try {
        if (onFinishTutorial) {
          onFinishTutorial()
        }
      } catch (e) {
        console.error(e)
      }

      return response
    } catch (e: any) {
      if (e?.invalid_fields) {
        setCustomErrors(e.invalid_fields)
        setIsLoading(false)
        throw e
      }
      const errorMessage = getErrorMessage(e)
      message?.open({
        type: 'error',
        content: errorMessage,
      })
      throw e
    } finally {
      setIsLoading(false)
    }
  }

  const setCustomErrors = (invalid_fields) => {
    // Contact Info Section
    if (invalid_fields.phone_country) {
      setCustomErroByField('phone', invalid_fields.phone_country)
      setActiveTab(ScheduleModalTabs.ContactInfo)
    }
    if (invalid_fields.support_website) {
      setCustomErroByField('support_website', invalid_fields.support_website)
      setActiveTab(ScheduleModalTabs.ContactInfo)
    }
    if (invalid_fields.support_email) {
      setCustomErroByField('support_email', invalid_fields.support_email)
      setActiveTab(ScheduleModalTabs.ContactInfo)
    }
    if (invalid_fields.support_phone) {
      setCustomErroByField('support_phone', invalid_fields.support_phone)
      setActiveTab(ScheduleModalTabs.ContactInfo)
    }
    if (invalid_fields.website) {
      setCustomErroByField('website', invalid_fields.website)
      setActiveTab(ScheduleModalTabs.ContactInfo)
    }
    if (invalid_fields.email) {
      setCustomErroByField('email', invalid_fields.email)
      setActiveTab(ScheduleModalTabs.ContactInfo)
    }
    if (invalid_fields.phone) {
      setCustomErroByField('phone', invalid_fields.phone)
      setActiveTab(ScheduleModalTabs.ContactInfo)
    }
    // End Contact Info Section
    // General Section
    if (invalid_fields.name_full) {
      setCustomErroByField('name', invalid_fields.name_full)
      setActiveTab(ScheduleModalTabs.General)
    }
    if (invalid_fields.name) {
      setCustomErroByField('name', invalid_fields.name)
      setActiveTab(ScheduleModalTabs.General)
    }
    if (invalid_fields.name) {
      setCustomErroByField('name_full', invalid_fields.name_full)
      setActiveTab(ScheduleModalTabs.General)
    }
    if (invalid_fields.abbreviation) {
      setCustomErroByField('abbreviation', invalid_fields.abbreviation)
      setActiveTab(ScheduleModalTabs.General)
    }
    if (invalid_fields.starts_at) {
      setCustomErroByField('starts_at', invalid_fields.starts_at)
      setActiveTab(ScheduleModalTabs.General)
    }
    if (invalid_fields.ends_at) {
      setCustomErroByField('ends_at', invalid_fields.ends_at)
      setActiveTab(ScheduleModalTabs.General)
    }
    if (invalid_fields.sport_id) {
      setCustomErroByField('sport_type', invalid_fields.sport_id)
      setActiveTab(ScheduleModalTabs.General)
    }
    if (invalid_fields.sport_type) {
      setCustomErroByField('sport_type', invalid_fields.sport_type)
      setActiveTab(ScheduleModalTabs.General)
    }
    // End General Section
  }

  const clearCustomErrorByField = (field: string) => {
    setCustomError((prev) => ({ ...prev, [field]: null }))
    setFieldErrors((prevErrors) => prevErrors.filter((e) => e !== field))
  }

  const setCustomErroByField = (field, lmessage) => {
    setCustomError((prev) => ({ ...prev, [field]: lmessage }))
    setFieldErrors((prevErrors) => [...new Set([...prevErrors, field])])
  }

  const getBase64 = (img, callback) => {
    const reader = new FileReader()
    reader.addEventListener('load', () => callback(reader.result))
    if (img) {
      reader.readAsDataURL(img)
    }
  }
  const beforeUpload = (file: File): boolean => {
    const isAllowedType = ALLOWED_IMAGE_TYPES.includes(file.type)
    const isLtMaxSize = file.size / 1024 / 1024 <= MAX_IMAGE_SIZE_MB
    if (!isAllowedType) {
      message?.open({
        type: 'error',
        content:
          'Please upload a file in one of the following formats: JPEG, GIF, PNG, AVIF, WebP, TIFF, or HEIC.',
        duration: 5,
      })
      return false
    }
    if (!isLtMaxSize) {
      message?.open({
        type: 'error',
        content: 'File is too large. Please upload an image smaller than 5MB.',
        duration: 5,
      })
      return false
    }
    return true
  }

  const handleChange = (info) => {
    try {
      setImageChanged(true)
      setIsLoading(true)
      getBase64(info.file.originFileObj, (url) => {
        setIsLoading(false)
        setImageUrl(url)
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleChangeBackground = (info) => {
    try {
      setBackgroundImageChanged(true)
      setIsLoading(true)
      getBase64(info.file.originFileObj, (url) => {
        setIsLoading(false)
        setBackgroundImageUrl(url)
      })
    } finally {
      setIsLoading(false)
    }
  }

  const onSubmitGeneralForm = async () => {
    try {
      if (isLoading) {
        return
      }
      if (!isEdit && !hasFilledNameAndAbbreviationAndDates) {
        return
      }
      if (isEdit && !hasFilledNameAndAbbreviationAndDates) {
        return
      }
      const response = await onSubmit()
      if (isEdit) {
        message?.open({
          type: 'success',
          content: (
            <div>
              <a
                href={ROUTES.SCHEDULE_ROOT + `/${response?.data?.id}/setup`}
                rel='noreferrer'
              >
                <Text
                  variant='paragraphSmall'
                  weight='regular'
                  color={colors.WHITE}
                  style={'text-decoration: underline;'}
                >{`${response?.data?.name_full} Updated Successfully`}</Text>
              </a>
            </div>
          ),
        })
      } else {
        message?.open({
          type: 'success',
          content: `${
            isCanlan ? 'Season' : 'Competition'
          } Created Successfully`,
        })
        setTimeout(() => {
          // history.push(ROUTES.SCHEDULE_ROOT + `/${response?.data?.id}/setup`)
          setIsCompetitionSetupModalOpen(true)
          setNewCompetitionId(response?.data?.id)
        }, 500)
      }
      triggerRefresh()
      closeForm()
    } catch (e) {
      console.error('Error submitting form', e)
    }
  }

  const onSubmitContactInfo = async () => {
    try {
      if (isLoading) {
        return
      }
      const response = await onSubmit()
      if (isEdit) {
        message?.open({
          type: 'success',
          content: (
            <div>
              <a
                href={ROUTES.SCHEDULE_ROOT + `/${response?.data?.id}/setup`}
                rel='noreferrer'
              >
                <Text
                  variant='paragraphSmall'
                  weight='regular'
                  color={colors.WHITE}
                  style={'text-decoration: underline;'}
                >{`${response?.data?.name_full} Updated Successfully`}</Text>
              </a>
            </div>
          ),
        })
      } else {
        message?.open({
          type: 'success',
          content: `${
            isCanlan ? 'Season' : 'Competition'
          } Created Successfully`,
        })
        setTimeout(() => {
          // history.push(ROUTES.SCHEDULE_ROOT + `/${response?.data?.id}/setup`)
          setIsCompetitionSetupModalOpen(true)
          setNewCompetitionId(response?.data?.id)
        }, 500)
      }
      triggerRefresh()
      closeForm()
    } catch (e: any) {
      const errorMessage = getErrorMessage(e)
      if (e?.invalid_fields) {
        if (e?.invalid_fields?.name || e?.invalid_fields?.abbreviation) {
          message?.open({
            type: 'error',
            content: errorMessage,
          })
        }
      }
    }
  }

  const onSubmitSettings = async () => {
    try {
      if (isLoading) {
        return
      }
      // First, validate the contact info
      if (invalidContactInfo) {
        setActiveTab(ScheduleModalTabs.ContactInfo)
        return
      }
      let website = contactInfo?.website || null
      let supportWebsite = contactInfo?.support_website || null
      if (website && !websiteRegex.test(website)) {
        setCustomErroByField('website', 'Invalid website')
        setActiveTab(ScheduleModalTabs.ContactInfo)
        return
      }
      if (supportWebsite && !websiteRegex.test(supportWebsite)) {
        setCustomErroByField('support_website', 'Invalid website')
        setActiveTab(ScheduleModalTabs.ContactInfo)
        return
      }
      if (website && !website?.startsWith('http')) {
        website = `https://${website}`
      }
      if (supportWebsite && !supportWebsite?.startsWith('http')) {
        supportWebsite = `https://${supportWebsite}`
      }
      // If no settings have changed, we don't need to reprocess the schedule
      if (!hasChangedSettings) {
        const response = await onSubmit()
        if (isEdit) {
          message?.open({
            type: 'success',
            content: (
              <div>
                <a
                  href={ROUTES.SCHEDULE_ROOT + `/${response?.data?.id}/setup`}
                  rel='noreferrer'
                >
                  <Text
                    variant='paragraphSmall'
                    weight='regular'
                    color={colors.WHITE}
                    style={'text-decoration: underline;'}
                  >{`${response?.data?.name_full} Updated Successfully`}</Text>
                </a>
              </div>
            ),
          })
        } else {
          message?.open({
            type: 'success',
            content: `${
              isCanlan ? 'Season' : 'Competition'
            } Created Successfully`,
          })
          closeForm()
          setTimeout(() => {
            // history.push(ROUTES.SCHEDULE_ROOT + `/${response?.data?.id}/setup`)
            setIsCompetitionSetupModalOpen(true)
            setNewCompetitionId(response?.data?.id)
          }, 500)
          return
        }
        triggerRefresh()
        closeForm()
      } else {
        // click the reprocess hidden button
        const reprocessHiddenButton = document.getElementById(
          'reprocess-hidden-button'
        )
        if (reprocessHiddenButton) {
          reprocessHiddenButton.click()
        }
      }
    } catch (e: any) {
      const errorMessage = getErrorMessage(e)
      if (e?.invalid_fields) {
        if (e?.invalid_fields?.name || e?.invalid_fields?.abbreviation) {
          setError(errorMessage)
        }
      }
    }
  }

  const onSubmitCustomization = async () => {
    try {
      if (isLoading) {
        return
      }
      const response = await onSubmit()
      if (isEdit) {
        // @ts-ignore
        message?.open({
          type: 'success',
          content: (
            <div>
              <a
                href={ROUTES.SCHEDULE_ROOT + `/${response?.data?.id}/setup`}
                rel='noreferrer'
              >
                <Text
                  variant='paragraphSmall'
                  weight='regular'
                  color={colors.WHITE}
                  style={'text-decoration: underline;'}
                >{`${response?.data?.name_full} Updated Successfully`}</Text>
              </a>
            </div>
          ),
        })
      } else {
        message?.open({
          type: 'success',
          content: `${
            isCanlan ? 'Season' : 'Competition'
          } Created Successfully`,
        })
        setTimeout(() => {
          // history.push(ROUTES.SCHEDULE_ROOT + `/${response?.data?.id}/setup`)
          setIsCompetitionSetupModalOpen(true)
          setNewCompetitionId(response?.data?.id)
        }, 500)
      }
      triggerRefresh()
      closeForm()
    } catch (e: any) {
      const errorMessage = getErrorMessage(e)
      if (e?.invalid_fields) {
        if (e?.invalid_fields?.name || e?.invalid_fields?.abbreviation) {
          setError(errorMessage)
        }
      }
    }
  }

  const onSubmitDaysmart = async () => {
    try {
      if (isLoading || isSyncing || !hasChanges) {
        return
      }
      await onSubmit()
      if (isEdit) {
        message?.open({
          type: 'success',
          content: `${
            isCanlan ? 'Season' : 'Competition'
          } Updated Successfully`,
        })
      } else {
        message?.open({
          type: 'success',
          content: `${
            isCanlan ? 'Season' : 'Competition'
          } Created Successfully`,
        })
      }
      triggerRefresh()
      closeForm()
    } catch (e: any) {
      const errorMessage = getErrorMessage(e)
      if (e?.invalid_fields) {
        if (e?.invalid_fields?.name || e?.invalid_fields?.abbreviation) {
          setError(errorMessage)
        }
      }
    }
  }

  return (
    <ScheduleContext.Provider
      value={{
        isSoccer,
        isGenericSport,
        sports,
        displayCanlanForm,
        id,
        sportOptions,
        genericId,
        isLoading,
        error,
        privacySettings,
        imageChanged,
        isEdit,
        name,
        abbreviation,
        taxRate,
        selectedSport,
        sportId,
        genericSportId,
        competitionDateRange,
        playoffCompetition,
        imageUrl,
        backgroundImageUrl,
        setBackgroundImageUrl,
        selectedSeasonType,
        onlineRegistrationOpen,
        selectedSeasonCurrency,
        isRegistrationEnabled,
        skipTeamOfficialsSignatures,
        skipOfficialsSignatures,
        skipStartingLineupSelection,
        requireGameOfficials,
        timingPeriodLength,
        timingExtraPeriodLength,
        regulationGameLength,
        goalsPerForfeit,
        forfeitLoss,
        wins,
        losses,
        ties,
        overtimerWin,
        overtimeLoss,
        shootoutLoss,
        fieldErrors,
        customError,
        setSportOptions,
        setIsLoading,
        setError,
        setPrivacySettings,
        setImageChanged,
        setName,
        setAbbreviation,
        setTaxRate,
        setSelectedSport,
        setSportId,
        setGenericSportId,
        setCompetitionDateRange,
        setPlayoffCompetition,
        setImageUrl,
        setSelectedSeasonType,
        setOnlineRegistrationOpen,
        setSelectedSeasonCurrency,
        setIsRegistrationEnabled,
        setSkipTeamOfficialsSignatures,
        setSkipOfficialsSignatures,
        setSkipStartingLineupSelection,
        setTimingPeriodLength,
        setTimingExtraPeriodLength,
        setRegulationGameLength,
        setRequireGameOfficials,
        setRequireGoalkeeper,
        requireGoalkeeper,
        requireRosters,
        setRequireRosters,
        setGoalsPerForfeit,
        setForfeitLoss,
        setWins,
        setLosses,
        setTies,
        setOvertimeWin,
        setOvertimeLoss,
        setShootoutLoss,
        setFieldErrors,
        setCustomError,
        onCancel,
        onSubmit,
        beforeUpload,
        handleChange,
        handleChangeBackground,
        genericSports,
        daySmartSeasonName,
        daySmartSyncInterval,
        daySmartSendInvitation,
        daySmartFacilitiesAsSubResources,
        setDaySmartSyncInterval,
        setDaySmartSendInvitation,
        setDaySmartFacilitiesAsSubResources,
        daySmartSeasonId,
        hasChangedSettings,
        contactInfo,
        setContactInfo,
        requireUniqueJerseyNumbers,
        setRequireUniqueJerseyNumbers,
        requireWaiverSignature,
        setRequireWaiverSignature,
        onSubmitGeneralForm,
        onSubmitContactInfo,
        onSubmitSettings,
        onSubmitCustomization,
        onSubmitDaysmart,
        isSyncing,
        setIsSyncing,
        message,
        hasFilledNameAndAbbreviationAndDates,
        isYouthLeague,
        setIsYouthLeague,
        disableTeamChat,
        setDisableTeamChat,
        backgroundImageChanged,
        setBackgroundImageChanged,
        isArchive,
        setIsArchive,
        requireTimeGameEvent,
        setRequireTimeGameEvent,
        requireGoalType,
        setRequireGoalType,
        requireGoalConfirmation,
        setRequireGoalConfirmation,
        assignPointsToPlayer,
        setAssignPointsToPlayer,
        allowTeamOfficialScoring,
        setAllowTeamOfficialScoring,
        defaultSortBy,
        setDefaultSortBy,
        tiebreakerStats,
        setTiebreakerStats,
        displayedStandings,
        setDisplayedStandings,
        displayedPlayerStats,
        setDisplayedPlayerStats,
        displayedGoalieStats,
        setDisplayedGoalieStats,
        playerStatsDefaultSortBy,
        setPlayerStatsDefaultSortBy,
        goalieStatsDefaultSortBy,
        setGoalieStatsDefaultSortBy,
        statTypes,
        defaultStandingsSettingsForSport,
        scoringValues,
        setScoringValues,
        setCustomErroByField,
        clearCustomErrorByField,
        activeTab,
        setActiveTab,
        playerStatsTypes,
        fullAllStatTypes,
      }}
    >
      {children}
    </ScheduleContext.Provider>
  )
}

// Custom hook to use the ScheduleContext
const useScheduleContext = () => {
  const context = useContext(ScheduleContext)
  if (context === undefined) {
    throw new Error('useScheduleContext must be used within a ScheduleProvider')
  }
  return context
}

export { ScheduleProvider, useScheduleContext }
